/**
 * GlobalFilterBar — dashboard-wide filter pills for date range, marketplace,
 * category, and product. Every filter is applied as a query-string param to
 * every /api/business-intelligence/* call the dashboard makes.
 */
import React from "react";
import { Filter, Calendar, Store, Boxes, Package, X } from "lucide-react";

const RANGES = [
  { key: "7d",  label: "7d",  days: 7 },
  { key: "30d", label: "30d", days: 30 },
  { key: "90d", label: "90d", days: 90 },
  { key: "1y",  label: "1y",  days: 365 },
  { key: "all", label: "All", days: null },
];

export function useFilters(initial = { range: "1y" }) {
  const [state, setState] = React.useState(initial);
  const query = React.useMemo(() => {
    const p = new URLSearchParams();
    const r = RANGES.find((x) => x.key === state.range);
    if (r?.days) {
      const from = new Date(Date.now() - r.days * 24 * 60 * 60 * 1000);
      p.set("date_from", from.toISOString());
    }
    if (state.marketplace) p.set("marketplace", state.marketplace);
    if (state.category)    p.set("category", state.category);
    if (state.product_id)  p.set("product_id", state.product_id);
    return p.toString();
  }, [state]);
  return { state, setState, query };
}

export default function GlobalFilterBar({ state, setState, marketplaces = [], categories = [], onOpenSearch }) {
  const set = (patch) => setState((s) => ({ ...s, ...patch }));
  const clear = () => setState({ range: "1y" });
  const anyActive = state.marketplace || state.category || state.product_id || state.range !== "1y";

  return (
    <div className="sticky top-0 z-20 -mx-4 sm:-mx-6 lg:-mx-8 border-b border-zinc-900 bg-zinc-950/85 backdrop-blur px-4 sm:px-6 lg:px-8 py-3 overflow-x-auto">
      <div className="max-w-7xl mx-auto flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <Filter className="h-3.5 w-3.5" /> Filters
        </div>

        {/* Range */}
        <div className="flex items-center gap-1 bg-zinc-900 border border-zinc-800 rounded-lg p-1">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => set({ range: r.key })}
              data-testid={`filter-range-${r.key}`}
              className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                state.range === r.key ? "bg-zinc-800 text-zinc-100" : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {marketplaces.length > 0 && (
          <FilterSelect icon={Store} value={state.marketplace} onChange={(v) => set({ marketplace: v || undefined })}
            label="Marketplace" testid="filter-marketplace" options={[{ v: "", l: "All marketplaces" }, ...marketplaces.map((m) => ({ v: m, l: m }))]} />
        )}
        {categories.length > 0 && (
          <FilterSelect icon={Boxes} value={state.category} onChange={(v) => set({ category: v || undefined })}
            label="Category" testid="filter-category" options={[{ v: "", l: "All categories" }, ...categories.map((c) => ({ v: c, l: c }))]} />
        )}

        <button
          onClick={onOpenSearch}
          data-testid="open-global-search"
          className="ml-auto inline-flex items-center gap-2 text-xs text-zinc-400 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded-lg px-3 py-1.5 transition-colors"
        >
          <span className="hidden sm:inline">Search</span>
          <kbd className="text-[10px] px-1.5 py-0.5 bg-zinc-800 rounded border border-zinc-700">⌘K</kbd>
        </button>

        {anyActive && (
          <button
            onClick={clear}
            data-testid="filters-clear"
            className="inline-flex items-center gap-1 text-xs text-orange-400 hover:text-orange-300"
          >
            <X className="h-3 w-3" /> Reset
          </button>
        )}
      </div>
    </div>
  );
}

function FilterSelect({ icon: Icon, value, onChange, label, options, testid }) {
  return (
    <label className="inline-flex items-center gap-2 text-xs">
      <Icon className="h-3.5 w-3.5 text-zinc-500" />
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="bg-zinc-900 border border-zinc-800 text-zinc-200 rounded-lg px-2 py-1 outline-none"
      >
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}
